import { queryOptions, useQuery } from '@tanstack/react-query';

import { ApiResponse, QueryOptions } from '@cast/shared-types';
import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

const apiFn = apiClient.dbo.getPerformanceAdvisorDatabases;

export type PerformanceAdvisorDatabasesQueryData = ApiResponse<typeof apiFn>;

export type PerformanceAdvisorDatabasesQueryArgs<SELECT> = QueryOptions<
  PerformanceAdvisorDatabasesQueryData,
  SELECT
> & {
  cacheGroupId?: string;
};

const DBO_PERFORMANCE_ADVISOR_DATABASES_QUERY_KEY =
  'dbo-performance-advisor-databases';

export const getPerformanceAdvisorDatabasesQueryKey = ({
  cacheGroupId,
}: { cacheGroupId?: string } = {}) =>
  cacheGroupId
    ? ([DBO_PERFORMANCE_ADVISOR_DATABASES_QUERY_KEY, cacheGroupId] as const)
    : ([DBO_PERFORMANCE_ADVISOR_DATABASES_QUERY_KEY] as const);

const getPerformanceAdvisorDatabasesQueryOptions = ({
  cacheGroupId,
  organizationId,
  enabled,
}: {
  cacheGroupId?: string;
  organizationId: string;
  enabled?: boolean;
}) =>
  queryOptions({
    queryKey: getPerformanceAdvisorDatabasesQueryKey({ cacheGroupId }),
    queryFn: async () => {
      const { data } = await apiFn({
        organizationId,
        cacheGroupId: cacheGroupId!,
        pageLimit: '1000',
      });
      return data;
    },
    enabled: makeIsEnabled(!!cacheGroupId, enabled),
  });

export const usePerformanceAdvisorDatabasesQuery = <
  SELECT = PerformanceAdvisorDatabasesQueryData
>({
  cacheGroupId,
  enabled,
  select,
  refetchInterval,
}: PerformanceAdvisorDatabasesQueryArgs<SELECT>) => {
  const { currentOrganization } = useOrganizations();

  return useQuery({
    ...getPerformanceAdvisorDatabasesQueryOptions({
      cacheGroupId,
      organizationId: currentOrganization!.id,
      enabled,
    }),
    select,
    refetchInterval,
  });
};
